/* Vulcain watch landing (sections/vulcain-landing.liquid)
   Reads the watches from the section markup, runs the loader + intro, the "Choose a watch"
   cursor, the ink bloom on hover, the synthesised sound, and opens the watch stories. */
(function () {
  "use strict";

  var NOTES = [261.63, 329.63, 392.0, 493.88, 587.33, 659.25];

  function setUrl(url) {
    try { history.replaceState(null, "", url); } catch (e) { /* ignore */ }
  }

  function init(stage) {
    if (!stage || stage.dataset.ready === "true") return null;
    stage.dataset.ready = "true";

    var alive = true;
    var ac = new AbortController();
    var on = function (target, type, fn, opts) {
      target.addEventListener(type, fn, Object.assign({ signal: ac.signal }, opts || {}));
    };

    var holder = stage.querySelector(".stones");
    var soundBtn = stage.querySelector(".sound");
    var cursor = stage.querySelector(".cursor");
    var discover = stage.querySelector(".discover");
    var loader = stage.querySelector(".loader");
    var shape = stage.querySelector(".loader__shape");
    var count = stage.querySelector(".loader__count");
    var title = stage.querySelector(".title");
    var discoverLabel = stage.dataset.discover || "Discover __NAME__";
    var designMode = stage.dataset.designMode === "true";

    var els = [].slice.call(holder.querySelectorAll(".stone"));
    var n = els.length;
    var WATCHES = els.map(function (el, i) {
      el.style.setProperty("--slot", String(i - (n - 1) / 2));
      return {
        id: el.dataset.id,
        name: el.dataset.name,
        color: el.dataset.color || "#888888",
        ink: el.dataset.ink || el.dataset.color || "#888888",
        note: NOTES[i % NOTES.length]
      };
    });

    /* ---------- sound (synthesised, no files) ---------- */
    var audio = null, soundOn = false;

    function initAudio() {
      if (audio) return;
      var Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) return;
      var ctx = new Ctx(), master = ctx.createGain();
      master.gain.value = 0;
      master.connect(ctx.destination);
      [130.81, 196.0, 261.63].forEach(function (f, i) {
        var o = ctx.createOscillator(), g = ctx.createGain();
        o.type = "sine";
        o.frequency.value = f;
        o.detune.value = (i - 1) * 6;
        g.gain.value = 0.03;
        o.connect(g).connect(master);
        o.start();
      });
      audio = { ctx: ctx, master: master };
    }

    function setSound(state) {
      soundOn = state;
      if (soundBtn) soundBtn.setAttribute("aria-pressed", String(state));
      if (state) initAudio();
      if (!audio) return;
      if (audio.ctx.state === "suspended") audio.ctx.resume();
      var t = audio.ctx.currentTime;
      audio.master.gain.cancelScheduledValues(t);
      audio.master.gain.setTargetAtTime(state ? 0.9 : 0, t, 0.4);
    }

    // a soft "voice": detuned oscillators with vibrato and a slow swell
    function sing(freq) {
      if (!soundOn || !audio) return;
      var ctx = audio.ctx, t = ctx.currentTime, env = ctx.createGain();
      env.gain.setValueAtTime(0, t);
      env.gain.linearRampToValueAtTime(0.08, t + 0.25);
      env.gain.exponentialRampToValueAtTime(0.0001, t + 2.4);
      env.connect(audio.master);
      var lfo = ctx.createOscillator(), lfoGain = ctx.createGain();
      lfo.frequency.value = 5.2;
      lfoGain.gain.value = 4;
      lfo.connect(lfoGain);
      [0, 7, -5].forEach(function (cents, i) {
        var o = ctx.createOscillator();
        o.type = i === 0 ? "triangle" : "sine";
        o.frequency.value = freq;
        o.detune.value = cents;
        lfoGain.connect(o.detune);
        o.connect(env);
        o.start(t);
        o.stop(t + 2.5);
      });
      lfo.start(t);
      lfo.stop(t + 2.5);
    }

    if (soundBtn) on(soundBtn, "click", function () { setSound(!soundOn); });

    /* ---------- ink bloom (watercolour cloud behind a hovered watch) ---------- */
    function bloomSVG(w, i) {
      var id = "vlink" + Math.random().toString(36).slice(2, 7) + i, seed = 7 + i * 13;
      return (
        '<svg viewBox="0 0 400 520" aria-hidden="true"><defs>' +
        '<radialGradient id="' + id + 'g" cx="50%" cy="52%" r="50%">' +
        '<stop offset="0" stop-color="' + w.ink + '" stop-opacity=".5"/>' +
        '<stop offset=".45" stop-color="' + w.ink + '" stop-opacity=".68"/>' +
        '<stop offset=".72" stop-color="' + w.ink + '" stop-opacity=".95"/>' +
        '<stop offset=".84" stop-color="' + w.ink + '" stop-opacity=".7"/>' +
        '<stop offset=".94" stop-color="' + w.ink + '" stop-opacity=".22"/>' +
        '<stop offset="1" stop-color="' + w.ink + '" stop-opacity="0"/></radialGradient>' +
        '<filter id="' + id + 'f" x="-40%" y="-40%" width="180%" height="180%" color-interpolation-filters="sRGB">' +
        '<feTurbulence type="fractalNoise" baseFrequency=".012" numOctaves="3" seed="' + seed + '" result="warp"/>' +
        '<feOffset class="ink-warp" in="warp" dx="0" dy="0" result="warpO"/>' +
        '<feDisplacementMap in="SourceGraphic" in2="warpO" scale="46" xChannelSelector="R" yChannelSelector="G" result="blob"/>' +
        '<feGaussianBlur in="blob" stdDeviation="6" result="blobSoft"/>' +
        '<feTurbulence type="fractalNoise" baseFrequency=".022" numOctaves="4" seed="' + (seed + 3) + '" result="mottle"/>' +
        '<feOffset class="ink-mottle" in="mottle" dx="0" dy="0" result="mottleO"/>' +
        '<feColorMatrix in="mottleO" type="matrix" values="0 0 0 0 0  0 0 0 0 0  0 0 0 0 0  0 0 0 .6 .58" result="mottleA"/>' +
        '<feComposite in="blobSoft" in2="mottleA" operator="in" result="patchy"/>' +
        '<feTurbulence type="fractalNoise" baseFrequency=".9" numOctaves="1" seed="' + (seed + 5) + '" result="grain"/>' +
        '<feColorMatrix in="grain" type="matrix" values="0 0 0 0 0  0 0 0 0 0  0 0 0 0 0  0 0 0 .55 .62" result="grainA"/>' +
        '<feComposite in="patchy" in2="grainA" operator="in"/>' +
        "</filter></defs>" +
        '<ellipse cx="200" cy="268" rx="165" ry="222" fill="url(#' + id + 'g)" filter="url(#' + id + 'f)"/>' +
        "</svg>"
      );
    }

    var blooms = WATCHES.map(function (w, i) {
      var d = document.createElement("div");
      d.className = "bloom";
      d.style.setProperty("--slot", String(i - (n - 1) / 2));
      d.innerHTML = bloomSVG(w, i);
      holder.insertBefore(d, els[0]); // behind every watch
      return d;
    });

    var hovered = null;
    function showBloom() {
      WATCHES.forEach(function (w, i) { blooms[i].classList.toggle("is-on", w === hovered); });
    }

    /* ---------- "Choose a watch" cursor + hover ---------- */
    var tx = innerWidth * 0.8, ty = innerHeight * 0.45;
    var cx = tx, cy = ty;

    function hoverWatch(w, e) {
      if (w && e) {
        var i = WATCHES.indexOf(w), r = blooms[i].getBoundingClientRect();
        if (r.width) {
          blooms[i].style.setProperty("--ox", ((e.clientX - r.left) / r.width * 100).toFixed(1) + "%");
          blooms[i].style.setProperty("--oy", ((e.clientY - r.top) / r.height * 100).toFixed(1) + "%");
        }
        tx = e.clientX;
        ty = e.clientY;
      }
      hovered = w;
      showBloom();
      if (cursor) cursor.classList.toggle("is-over", !!w);
      if (discover) {
        discover.classList.toggle("is-on", !!w);
        if (w) discover.textContent = discoverLabel.replace("__NAME__", w.name);
      }
      if (w) sing(w.note);
    }

    els.forEach(function (el, i) {
      var w = WATCHES[i];
      on(el, "pointerenter", function (e) { hoverWatch(w, e); });
      on(el, "pointerleave", function () { hoverWatch(null); });
      on(el, "focus", function () { sing(w.note); });
      on(el, "click", function () {
        sing(w.note / 2);
        if (window.VulcainStory && window.VulcainStory.has(w.id)) window.VulcainStory.open(w.id, stage);
      });
    });

    on(document, "storyclose", function () {
      hoverWatch(null);
      if (cursor) cursor.classList.remove("is-down");
    });

    on(window, "pointermove", function (e) {
      if (e.pointerType !== "mouse") return;
      tx = e.clientX;
      ty = e.clientY;
      // no "Choose a watch" ring over the Sound toggle or the button: the normal hand pointer shows there
      if (cursor) cursor.classList.toggle("is-hidden", !!e.target.closest(".top, .cta"));
    });
    on(document.documentElement, "mouseleave", function () { if (cursor) cursor.classList.add("is-hidden"); });
    on(document.documentElement, "mouseenter", function () { if (cursor) cursor.classList.remove("is-hidden"); });
    on(window, "pointerdown", function () { if (cursor) cursor.classList.add("is-down"); });
    on(window, "pointerup", function () { if (cursor) cursor.classList.remove("is-down"); });

    // the ink leans toward the cursor and its edge reshapes only while the cursor moves
    var ink = WATCHES.map(function (w, i) {
      return { x: 0, y: 0, phase: 0, shown: 0,
        warp: blooms[i].querySelector(".ink-warp"), mottle: blooms[i].querySelector(".ink-mottle") };
    });
    var INK_FOLLOW = 0.06, INK_FLOW = 0.012, INK_REACH = 46;
    var lastX = null, lastY = null;

    on(window, "pointermove", function (e) {
      if (e.pointerType !== "mouse") return;
      if (hovered && lastX !== null) {
        var d = Math.hypot(e.clientX - lastX, e.clientY - lastY);
        ink[WATCHES.indexOf(hovered)].phase += Math.min(d, 60) * INK_FLOW;
      }
      lastX = e.clientX;
      lastY = e.clientY;
    });

    function moveInk() {
      WATCHES.forEach(function (w, i) {
        var p = ink[i], goalX = 0, goalY = 0;
        if (w === hovered) {
          var r = els[i].getBoundingClientRect();
          var mx = r.width * 0.42, my = r.height * 0.3;
          goalX = Math.max(-mx, Math.min(mx, (cx - (r.left + r.width / 2)) * 0.16));
          goalY = Math.max(-my, Math.min(my, (cy - (r.top + r.height / 2)) * 0.12));
        } else if (!blooms[i].classList.contains("is-on")) {
          return;
        }
        var vx = (goalX - p.x) * INK_FOLLOW, vy = (goalY - p.y) * INK_FOLLOW;
        p.x += vx;
        p.y += vy;
        var sx = 1 + Math.min(Math.abs(vx) * 0.006, 0.04);
        var sy = 1 + Math.min(Math.abs(vy) * 0.006, 0.04);
        blooms[i].style.setProperty("--bx", p.x.toFixed(1) + "px");
        blooms[i].style.setProperty("--by", p.y.toFixed(1) + "px");
        blooms[i].firstChild.style.transform = "scale(" + sx.toFixed(3) + "," + sy.toFixed(3) + ")";
        var dp = p.phase - p.shown;
        if (Math.abs(dp) > 0.0005) {
          p.shown += dp * 0.12;
          var a = p.shown;
          p.warp.setAttribute("dx", (Math.sin(a) * INK_REACH).toFixed(2));
          p.warp.setAttribute("dy", (Math.cos(a * 0.83) * INK_REACH).toFixed(2));
          p.mottle.setAttribute("dx", (Math.sin(a * 0.6 + 1) * INK_REACH * 0.6).toFixed(2));
          p.mottle.setAttribute("dy", (Math.cos(a * 0.5) * INK_REACH * 0.6).toFixed(2));
        }
      });
    }

    var depth = WATCHES.map(function (w, i) { return i === (n - 1) / 2 ? 0.5 : 0.8; });
    (function follow() {
      if (!alive) return;
      cx += (tx - cx) * 0.18;
      cy += (ty - cy) * 0.18;
      if (cursor) cursor.style.transform = "translate3d(" + cx.toFixed(1) + "px," + cy.toFixed(1) + "px,0) translateY(-50%)";
      var dx = cx / innerWidth - 0.5, dy = cy / innerHeight - 0.5;
      els.forEach(function (el, i) {
        var img = el.querySelector(".stone__img");
        if (img) img.style.transform = "translate(" + (dx * depth[i]).toFixed(3) + "vw," + (dy * depth[i]).toFixed(3) + "vw)";
      });
      moveInk();
      requestAnimationFrame(follow);
    })();

    /* ---------- loader + intro timeline ---------- */
    var SHAPES = WATCHES.map(function (w, i) {
      var size = [96, 108, 92, 100, 88][i % 5];
      return { at: Math.round((i / Math.max(1, n)) * 90), w: size, h: size, r: "50%", c: w.color };
    }).concat([{ at: 100, w: 58, h: 62, r: "12px", c: "#7d838b" }]);
    var MIN_LOADER = 1100;
    var timers = [];
    function later(ms, fn) { var t = setTimeout(function () { if (alive) fn(); }, ms); timers.push(t); return t; }

    function setShape(pct) {
      if (!shape) return;
      var s = SHAPES[0];
      SHAPES.forEach(function (x) { if (pct >= x.at) s = x; });
      shape.style.setProperty("--lw", s.w + "px");
      shape.style.setProperty("--lh", s.h + "px");
      shape.style.setProperty("--lr", s.r);
      shape.style.setProperty("--lc", s.c);
    }

    function placeTitleForIntro() {
      if (!title) return;
      title.style.transform = "none";
      var r = title.getBoundingClientRect();
      var dx = innerWidth / 2 - (r.left + r.width / 2);
      var dy = innerHeight * 0.48 - (r.top + r.height / 2);
      var line = title.firstElementChild ? title.firstElementChild.getBoundingClientRect().width : r.width;
      var scale = Math.min(1.5, (innerWidth * 0.86) / Math.max(1, line));
      title.style.transform = "translate(" + dx + "px," + dy + "px) scale(" + scale.toFixed(3) + ")";
    }

    function openFromHash() {
      var id = location.hash.slice(1);
      if (id && window.VulcainStory && window.VulcainStory.has(id)) window.VulcainStory.open(id, stage);
    }

    function goLive() {
      stage.classList.remove("is-loading", "is-intro", "is-settle");
      stage.classList.add("is-title", "is-live");
      if (loader) loader.remove();
    }

    function runIntro() {
      if (count) count.classList.add("is-out");
      later(150, function () {
        if (shape) shape.classList.add("is-fading");
        if (loader) loader.classList.add("is-done");
      });
      later(500, function () {
        if (loader) loader.classList.add("is-gone");
        placeTitleForIntro();
        stage.classList.remove("is-loading");
        stage.classList.add("is-intro");
      });
      later(1400, function () { stage.classList.add("is-title"); });
      later(2900, function () {
        stage.classList.remove("is-intro");
        stage.classList.add("is-settle");
      });
      later(4400, function () {
        stage.classList.remove("is-settle");
        stage.classList.add("is-live");
        if (loader) loader.remove();
        openFromHash();
      });
    }

    if (designMode || !loader) {
      // in the theme editor, skip the intro so every edit shows the page straight away
      goLive();
    } else {
      var start = performance.now(), shown = 0, loaded = false;
      var fontsReady = document.fonts ? document.fonts.ready : Promise.resolve();
      var pageReady = new Promise(function (res) {
        if (document.readyState === "complete") res();
        else window.addEventListener("load", res, { once: true });
      });
      Promise.all([fontsReady, pageReady]).then(function () { loaded = true; });
      later(8000, function () { loaded = true; });

      (function tick() {
        if (!alive) return;
        var elapsed = performance.now() - start;
        var target = Math.min(100, (elapsed / MIN_LOADER) * 100);
        if (!loaded) target = Math.min(target, 96);
        shown = Math.max(shown, Math.floor(target));
        if (count) count.textContent = shown + "%";
        if (loader) {
          loader.style.setProperty("--p", shown + "%");
          loader.setAttribute("aria-valuenow", String(shown));
        }
        setShape(shown);
        if (shown >= 100) return later(250, runIntro);
        later(30, tick);
      })();

      on(loader, "click", function () { if (!soundOn) setSound(true); });
    }

    on(window, "resize", function () {
      if (stage.classList.contains("is-intro")) placeTitleForIntro();
    });

    /* ---------- phones and tablets: a little tour instead of a mouse cursor ----------
       Every few seconds the "Choose a watch" ring glides to a random watch and plays its hover
       (ink + "Discover ..."), so customers see the watches can be tapped. Desktop is unaffected. */
    var isTouch = matchMedia("(hover: none), (pointer: coarse)").matches;
    if (isTouch) {
      stage.classList.add("is-touch");
      var lastPick = -1, pausedUntil = 0, wiggle = null;
      var isCopy = stage.hasAttribute("data-vulcain-copy");
      var busy = function () {
        if (!stage.classList.contains("is-live")) return true;
        if (!isCopy && document.documentElement.classList.contains("story-lock")) return true; // a story covers the landing
        return performance.now() < pausedUntil;
      };
      var stopWiggle = function () { if (wiggle) { clearInterval(wiggle); wiggle = null; } };
      var hop = function () {
        if (!alive) return;
        if (busy()) return later(700, hop);
        var i = Math.floor(Math.random() * n);
        if (n > 1 && i === lastPick) i = (i + 1 + Math.floor(Math.random() * (n - 1))) % n;
        lastPick = i;
        var img = els[i].querySelector(".stone__img") || els[i];
        var r = img.getBoundingClientRect();
        var px = r.left + r.width * (0.38 + Math.random() * 0.24);
        var py = r.top + r.height * (0.4 + Math.random() * 0.2);
        tx = px; ty = py; // the ring glides there (eased by the follow loop)
        later(700, function () {
          if (busy()) return;
          hoverWatch(WATCHES[i], { clientX: px, clientY: py });
          // drift a little while "hovering", so the ink reshapes as it does under a moving mouse
          var k = 0;
          stopWiggle();
          wiggle = setInterval(function () {
            if (!alive || hovered !== WATCHES[i]) return stopWiggle();
            k += 1;
            tx = px + Math.sin(k / 3) * r.width * 0.08;
            ty = py + Math.cos(k / 4) * r.height * 0.04;
            ink[i].phase += 0.22;
          }, 90);
          timers.push(wiggle);
        });
        later(700 + 2300, function () {
          stopWiggle();
          if (hovered === WATCHES[i]) hoverWatch(null);
        });
        later(700 + 2300 + 900, hop);
      };
      // a real touch takes over for a moment
      on(window, "pointerdown", function (e) {
        if (e.pointerType === "mouse") return;
        pausedUntil = performance.now() + 4500;
        stopWiggle();
      });
      later(900, hop);
    }

    return function destroy() {
      alive = false;
      ac.abort();
      timers.forEach(function (t) { clearTimeout(t); clearInterval(t); });
      if (audio) audio.ctx.close();
    };
  }

  /* ---------- boot + theme editor support ---------- */
  var instances = new Map();

  function boot(scope) {
    (scope || document).querySelectorAll("[data-vulcain-landing]").forEach(function (stage) {
      var destroy = init(stage);
      if (destroy) instances.set(stage, destroy);
    });
  }

  document.addEventListener("shopify:section:unload", function (e) {
    instances.forEach(function (destroy, stage) {
      if (e.target.contains(stage)) { destroy(); instances.delete(stage); }
    });
  });
  document.addEventListener("shopify:section:load", function (e) { boot(e.target); });

  // used by vulcain-story.js to run the same landing on the copy at the end of a story
  window.VulcainLanding = {
    mount: function (stage) { var d = init(stage); if (d) instances.set(stage, d); },
    unmount: function (stage) { var d = instances.get(stage); if (d) { d(); instances.delete(stage); } }
  };

  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", function () { boot(); });
  else boot();


  


const buttons = document.querySelectorAll('#kaviyo-cta');

buttons.forEach(button => {
  button.addEventListener('click', () => {
    ShowPopUp()
  });
});



function ShowPopUp() {

  window._klOnsite = window._klOnsite || [];
  window._klOnsite.push(['openForm', 'Th27E4']);

}

window.addEventListener("klaviyoForms", function (e) {
  if (e.detail.type == 'submit') {
    // add Facebook tracking pixel code here
    !function (f, b, e, v, n, t, s) {
      if (f.fbq) return; n = f.fbq = function () {
        n.callMethod ?
          n.callMethod.apply(n, arguments) : n.queue.push(arguments)
      };
      if (!f._fbq) f._fbq = n; n.push = n; n.loaded = !0; n.version = '2.0';
      n.queue = []; t = b.createElement(e); t.async = !0;
      t.src = v; s = b.getElementsByTagName(e)[0];
      s.parentNode.insertBefore(t, s)
    }(window, document, 'script',
      'https://connect.facebook.net/en_US/fbevents.js');
    fbq('init', '225981648771540');
    fbq('track', 'Lead');
  }
});

})();
